import { HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { BrowserModule, DomSanitizer, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { MetaReducer, StoreModule } from '@ngrx/store';
import { FullscreenLayoutAreas } from '@src/assets/css/fullscreen.template.scss';

import { AppComponent } from './app.component';
import { AppErrorHandler } from './app.errorhandler';
import { initializeAppFactory } from './app.init';
import { AppRoutingModule } from './app.routing';
import { NomWebServicesJson } from './common/services/nom-web-services-json';
import { CastStateEffects } from './common/store/cast/cast.effects';
import { castReducer } from './common/store/cast/cast.reducers';
import { CreateViewStateEffects } from './common/store/createviewstate/createviewstate.effects';
import { createViewStateReducer } from './common/store/createviewstate/createviewstate.reducer';
import { errorHandlingMetaReducer } from './common/store/error/handler.meta.reducer';
import { fourthSidecarReducer, FullscreenModalEffects, fullscreenNewRecordReducer, fullscreenReducer, mainSidecarReducer, secondSidecarReducer, thirdSidecarReducer } from './common/store/fullscreen';
import { GlobalTablesEffects } from './common/store/global-tables/global-tables.effects';
import { globalTablesReducer } from './common/store/global-tables/global-tables.reducers';
import { helloResultsReducer } from './common/store/hello-results/hello-results.reducers';
import { i18nReducer } from './common/store/i18n/reducers/i18n.reducers';
import { ListEffects } from './common/store/list/list.effects';
import { listReducer } from './common/store/list/list.reducers';
import { MerlinSearchEffects } from './common/store/merlin/effects/ai.search.effects';
import { merlinSearchReducer } from './common/store/merlin/reducers/ai.search.reducer';
import { MosEffects } from './common/store/mos/effects/mos.effects';
import { mosReducer } from './common/store/mos/reducers/mos.reducer';
import { NavigationEffects } from './common/store/navigation/effects/navigation.effects';
import { ViewStateEffects } from './common/store/navigation/effects/viewstate.effects';
import { navigationReducer } from './common/store/navigation/reducers/navigation.reducers';
import { viewStateReducer } from './common/store/navigation/reducers/viewstate.reducers';
import { PlaybookEffects } from './common/store/playbook/playbook.effects';
import { playbookReducer } from './common/store/playbook/playbook.reducers';
import { RecordEffects } from './common/store/records/records.effects';
import {
    assignmentRecordsReducer,
    discoverRecordsReducer,
    publicationRecordsReducer,
    rundownRecordsReducer,
    storiesRecordsReducer,
    topicRecordsReducer,
    userRecordsReducer,
} from './common/store/records/records.reducers';
import { StoryEffects } from './common/store/story/effects/story.effects';
import { storiesReducer } from './common/store/story/reducer/story.reducers';
import { StorytellingCommonModule } from './common/storytelling-common.module';
import { DiscoverEffects } from './discover/store/effects/discover.effects';
import { FeedProviderEffects } from './discover/store/effects/feed.provider.effects';
import { wiresReducer } from './discover/store/reducers/discover.reducer';
import { feedProviderReducer } from './discover/store/reducers/feed.provider.reducer';
import { LiveEffects } from './live/store/effects/live.effects';
import { liveReducer } from './live/store/reducers/live.reducers';

const metaReducers: MetaReducer<any>[] = [errorHandlingMetaReducer];

@NgModule({
    declarations: [AppComponent],
    imports: [
        RouterModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        StorytellingCommonModule,
        BrowserModule,
        StoreModule.forRoot(
            {
                discover: wiresReducer,
                navigation: navigationReducer,
                viewstate: viewStateReducer,
                i18n: i18nReducer,
                stories: storiesReducer,
                mos: mosReducer,
                live: liveReducer,
                feedprovider: feedProviderReducer,
                merlinSearchResults: merlinSearchReducer,
                topicRecords: topicRecordsReducer,
                assignmentRecords: assignmentRecordsReducer,
                storyRecords: storiesRecordsReducer,
                rundownRecords: rundownRecordsReducer,
                userRecords: userRecordsReducer,
                fullscreenDialog: fullscreenReducer,
                fullscreenNewRecordDialog: fullscreenNewRecordReducer,
                createviewstate: createViewStateReducer,
                list: listReducer,
                globalTables: globalTablesReducer,
                helloResults: helloResultsReducer,
                playbook: playbookReducer,
                discoverRecords: discoverRecordsReducer,
                publicationRecords: publicationRecordsReducer,
                [FullscreenLayoutAreas.SIDECAR_MAIN]: mainSidecarReducer,
                [FullscreenLayoutAreas.SIDECAR_SECOND]: secondSidecarReducer,
                [FullscreenLayoutAreas.SIDECAR_THIRD]: thirdSidecarReducer,
                [FullscreenLayoutAreas.SIDECAR_FOURTH]: fourthSidecarReducer,
                cast: castReducer,
            },
            {
                runtimeChecks: {
                    strictStateImmutability: true,
                    strictActionImmutability: true,
                    strictStateSerializability: true,
                    strictActionSerializability: true,
                    strictActionWithinNgZone: true,
                    strictActionTypeUniqueness: true,
                },
                metaReducers,
            }
        ),
        EffectsModule.forRoot([
            PlaybookEffects,
            DiscoverEffects,
            NavigationEffects,
            MosEffects,
            StoryEffects,
            LiveEffects,
            FeedProviderEffects,
            MerlinSearchEffects,
            FullscreenModalEffects,
            RecordEffects,
            ViewStateEffects,
            CastStateEffects,
            CreateViewStateEffects,
            GlobalTablesEffects,
            ListEffects,
        ]),
        HttpClientModule,
    ],
    providers: [
        {
            provide: ErrorHandler,
            useClass: AppErrorHandler,
        },
        Title,
        {
            provide: APP_INITIALIZER,
            useFactory: initializeAppFactory,
            deps: [HttpClient, NomWebServicesJson, MatIconRegistry, DomSanitizer],
            multi: true,
        },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
